// ===========================================================================
// HighCostPatientPage — Ficha del paciente (/high-cost/:cipId).
// Cabecera + resumen financiero (desglose + trayectoria) + perfil clínico +
// perfil social (con acceso al editor de estratificación).
// ===========================================================================
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Box, Button, CircularProgress, Alert, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getPatientDetail } from "../services";
import { getSocialProfile, buildDefaultSocialProfile } from "../services";
import { HIGH_COST_BASE_PATH } from "../common/constants";
import PatientHeader from "../components/Patient/PatientHeader";
import FinancialSummary from "../components/Patient/FinancialSummary";
import ClinicalProfile from "../components/Patient/ClinicalProfile";
import SocialSummary from "../components/Patient/SocialSummary";
import SocialStratificationPanel from "../components/Stratification/SocialStratificationPanel";

export default function HighCostPatientPage() {
  const { cipId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [socialProfile, setSocialProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getPatientDetail(cipId);
        if (active) setDetail(data);
      } catch (err) {
        if (active) {
          setError(err?.response?.status === 404 ? "No High Cost record found for this patient." : "Could not load patient detail.");
        }
      } finally {
        if (active) setLoading(false);
      }

      // Perfil social: endpoint propuesto; si falla, fallback al perfil por defecto.
      try {
        const sp = await getSocialProfile(cipId);
        if (active) setSocialProfile(sp);
      } catch {
        if (active) setSocialProfile(buildDefaultSocialProfile(cipId));
      }
    })();
    return () => {
      active = false;
    };
  }, [cipId]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(HIGH_COST_BASE_PATH)} sx={{ mb: 2, textTransform: "none" }}>
        Back to High Cost
      </Button>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="warning">{error}</Alert>}

      {!loading && !error && detail?.header && (
        <>
          <PatientHeader header={detail.header} socialCategory={socialProfile?.globalCategory} />
          <FinancialSummary header={detail.header} cipId={cipId} />
          <ClinicalProfile conditions={detail.conditions} header={detail.header} />
          <SocialSummary profile={socialProfile} onReview={() => setPanelOpen(true)} />
          <SocialStratificationPanel
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            cipId={cipId}
            profile={socialProfile}
          />
        </>
      )}
    </Container>
  );
}
